import { zodResolver } from '@hookform/resolvers/zod'
import type { BackofficeAuthAcceptInviteData } from '@repo/sdk/gen'
import { backofficeAuthAcceptInvite } from '@repo/sdk/gen'
import { Button } from '@repo/ui/button'
import { ButtonLink } from '@repo/ui/components/button-link'
import { InputPassword } from '@repo/ui/components/input-password'
import { Field, FieldError, FieldGroup, FieldLabel } from '@repo/ui/field'
import {
	AuthCard,
	AuthCardBody,
	AuthCardHeader
} from '@repo/ui/modules/auth/components/auth-card'
import { AuthErrorAlert } from '@repo/ui/modules/auth/components/auth-error-alert'
import { Spinner } from '@repo/ui/spinner'
import { formatError } from '@repo/ui/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { useTranslation } from '@/lib/i18n'
import { fetchFreshSession } from '@/query/query-options'
import { AuthBrand } from '../-components/auth-brand'
import { SetPasswordSchema } from '../-lib/form-schemas'

export const Route = createFileRoute('/(auth)/accept-invite/')({
	validateSearch: z.object({
		token: z.string().optional()
	}),
	component: AdminAcceptInvite
})

function AdminAcceptInvite() {
	const { t } = useTranslation()
	const { token } = Route.useSearch()

	if (!token) {
		return (
			<AuthCard>
				<AuthCardHeader
					description={t(
						'auth:accept_invite.invalid_link_description'
					)}
					icon={<AuthBrand />}
					title={t('auth:accept_invite.invalid_link_title')}
				/>
				<AuthCardBody>
					<div className='flex justify-center'>
						<ButtonLink to='/login' variant='link'>
							{t('auth:back_to_login')}
						</ButtonLink>
					</div>
				</AuthCardBody>
			</AuthCard>
		)
	}

	return (
		<AuthCard>
			<AuthCardHeader
				description={t('auth:accept_invite.description')}
				icon={<AuthBrand />}
				title={t('auth:accept_invite.title')}
			/>
			<AuthCardBody>
				<AcceptInviteForm token={token} />
			</AuthCardBody>
		</AuthCard>
	)
}

function AcceptInviteForm(props: { token: string }) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const queryClient = useQueryClient()

	const form = useForm<z.infer<typeof SetPasswordSchema>>({
		resolver: zodResolver(SetPasswordSchema),
		defaultValues: { newPassword: '', confirmPassword: '' }
	})

	const acceptInvite = useMutation({
		mutationFn: (body: BackofficeAuthAcceptInviteData['body']) =>
			backofficeAuthAcceptInvite({ body }),
		onSuccess: async () => {
			toast.success(t('auth:accept_invite.success'))
			// Accepting the invite signs the admin in: revalidate the cached
			// session before navigating so the private guard reads the fresh
			// state.
			await fetchFreshSession(queryClient)
			await navigate({ replace: true, to: '/' })
		}
	})

	return (
		<form
			onSubmit={form.handleSubmit((data) =>
				acceptInvite.mutate({
					token: props.token,
					password: data.newPassword
				})
			)}
		>
			<FieldGroup>
				{acceptInvite.isError && (
					<AuthErrorAlert
						onClose={() => acceptInvite.reset()}
						title={formatError(acceptInvite.error)}
					/>
				)}
				<Controller
					control={form.control}
					name='newPassword'
					render={({ field, fieldState }) => (
						<Field data-invalid={fieldState.invalid}>
							<FieldLabel htmlFor={field.name}>
								{t('common:new_password')}
							</FieldLabel>
							<InputPassword
								{...field}
								aria-invalid={fieldState.invalid}
								autoComplete='new-password'
								id={field.name}
							/>
							{fieldState.invalid && (
								<FieldError errors={[fieldState.error]} />
							)}
						</Field>
					)}
				/>
				<Controller
					control={form.control}
					name='confirmPassword'
					render={({ field, fieldState }) => (
						<Field data-invalid={fieldState.invalid}>
							<FieldLabel htmlFor={field.name}>
								{t('auth:confirm_password')}
							</FieldLabel>
							<InputPassword
								{...field}
								aria-invalid={fieldState.invalid}
								autoComplete='new-password'
								id={field.name}
							/>
							{fieldState.invalid && (
								<FieldError errors={[fieldState.error]} />
							)}
						</Field>
					)}
				/>
				<Field>
					<Button disabled={acceptInvite.isPending} type='submit'>
						{acceptInvite.isPending ? (
							<Spinner />
						) : (
							t('auth:accept_invite.submit')
						)}
					</Button>
				</Field>
			</FieldGroup>
		</form>
	)
}
